import { Link } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';
import { AREAS, CHAIN, SOUL_DNA_CAPTION, SOUL_DNA_IMAGE } from '../../lib/u99';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { PageHead, Chip } from '../../components/u99/PageHead';
import BookFeature from '../../components/u99/BookFeature';

const THREADS = AREAS.filter((a) => a.id !== 'home' && a.id !== 'mind');

export default function KylesMind() {
  return (
    <main className="max-w-7xl mx-auto px-5 lg:px-10" data-testid="kyles-mind-page">
      <PageHead eyebrow="Kyle's Mind · The connecting identity" accent="#22D3EE" image="/u99/helix-snowflake-blood-cells.jpg" imageAlt="Orange double helix with snowflake blood cells — the internal binary flow of the soul DNA" title={<>One mind. <span className="text-cyan-300">Several rooms.</span></>} lede="Placeholder biography. Who Kyle Hinton is, what he is trying to do, and why the doctrines, the tools and the documents belong to the same enquiry. Two or three honest paragraphs, written in first person." />

      <section className="grid lg:grid-cols-12 gap-10 py-10 lg:py-16 border-t border-white/[0.07]">
        <Reveal className="lg:col-span-4">
          <Eyebrow accent="#22D3EE">The spine</Eyebrow>
          <p className="text-sm sm:text-base text-slate-400 leading-relaxed">Placeholder. Every area on the site touches the same seven-link sequence. It is the one idea the rest depends on.</p>
        </Reveal>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="mind-chain">
          {CHAIN.map((c, i) => (
            <Reveal key={c} delay={i * 0.05} className="sm:text-center">
              <div className="rounded-xl border border-white/[0.07] px-4 py-4 sm:py-6 flex sm:flex-col items-center gap-3 sm:gap-2">
                <span className="font-celestial text-cyan-300/70 text-sm">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-200">{c}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-10 lg:py-16 border-t border-white/[0.07] grid lg:grid-cols-12 gap-10 items-center" data-testid="soul-dna-section">
        <Reveal className="lg:col-span-5">
          <Eyebrow accent="#22D3EE">Soul DNA · The internal binary flow</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl text-slate-100 leading-tight">Snowflakes as <span className="text-cyan-300">blood cells</span></h2>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed" data-testid="soul-dna-caption">{SOUL_DNA_CAPTION}</p>
          <Link to="/u99/documents/spiritual-physics-the-biology-of-the-cosmos" className="mt-8 inline-flex items-center gap-3 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-300 hover:text-cyan-300 transition-colors" data-testid="soul-dna-thesis-link">Read Spiritual Physics <ArrowRight size={14} /></Link>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <figure className="rounded-3xl overflow-hidden border border-cyan-400/20 shadow-[0_60px_120px_-40px_rgba(34,211,238,0.25)]">
            <img src={SOUL_DNA_IMAGE} alt={SOUL_DNA_CAPTION} className="w-full h-auto block" loading="lazy" />
            <figcaption className="px-6 py-4 border-t border-cyan-400/15 bg-[#04070f] font-mono-precision text-[10px] sm:text-[11px] uppercase tracking-[0.2em] leading-relaxed text-cyan-200/80">{SOUL_DNA_CAPTION}</figcaption>
          </figure>
        </Reveal>
      </section>

      <section className="py-10 lg:py-16 border-t border-white/[0.07]">
        <Reveal><Eyebrow accent="#22D3EE">The foundational book</Eyebrow></Reveal>
        <BookFeature accent="#22D3EE" />
      </section>

      <section className="py-10 lg:py-16 border-t border-white/[0.07]">
        <Reveal><Eyebrow accent="#22D3EE">Threads</Eyebrow></Reveal>
        <div className="grid md:grid-cols-3 gap-4">
          {THREADS.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.07}>
              <Link to={a.to} className="group block rounded-2xl border border-white/[0.07] hover:border-cyan-300/40 p-6 transition-colors duration-500" data-testid={`mind-thread-${a.id}`}>
                <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">Leads to</p>
                <h3 className="mt-2 font-celestial text-xl text-slate-100 group-hover:text-cyan-100 transition-colors">{a.label}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">Placeholder — how this room connects back to Kyle's Mind.</p>
                <span className="mt-5 inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 group-hover:text-cyan-300 transition-colors">Follow <ArrowRight size={12} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="future" className="py-10 lg:py-16 border-t border-white/[0.07]" data-testid="future-section">
        <Reveal>
          <div className="rounded-3xl border border-dashed border-amber-400/30 p-7 lg:p-12 relative overflow-hidden">
            <span className="absolute right-6 top-6 text-amber-400/40"><Lock size={18} /></span>
            <Chip testId="future-chip">Future · held for later</Chip>
            <h2 className="mt-6 font-celestial text-3xl sm:text-4xl text-slate-100 leading-tight">Polarity <span className="text-slate-500">&</span> Whisp</h2>
            <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl">Deeper development of Polarity and the Whisp is deliberately parked. This week's effort is the public foundation: structure, one document, one doctrine template, one tool. When this section opens it will be clearly marked as such.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-2xl">
              {[['Polarity', 'Placeholder — the lean of a bloodline, name and personality; the correction that follows.'], ['Whisp', 'Placeholder — reserved. Description arrives with the section.']].map(([t, d]) => (
                <div key={t} className="rounded-2xl border border-white/[0.06] p-5 opacity-70" data-testid={`future-card-${t.toLowerCase()}`}>
                  <p className="font-celestial text-lg text-slate-200">{t}</p>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
